// src/pages/SR_EngineerChecklist.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { sr } from "../api/serviceReports";
import SRSignaturePad from "../components/SR_SignaturePad";

/* -------------------------------- Helpers -------------------------------- */

function getApiBase() {
  if (sr && (sr.baseUrl || sr.API_BASE_URL || sr.apiBase || sr.API)) {
    return sr.baseUrl || sr.API_BASE_URL || sr.apiBase || sr.API || "";
  }
  if (typeof window !== "undefined" && window.API_BASE_URL) return window.API_BASE_URL;
  return "";
}

function extractIds(location) {
  const q = new URLSearchParams(location.search || "");
  const from = (obj, keys) => { for (const k of keys) if (obj && obj[k] != null) return obj[k]; return null; };
  const jobRaw = q.get("job") || q.get("job_id") || q.get("jid") ||
                 from(location.state || {}, ["job", "job_id", "jid", "jobId"]);
  const tplRaw = q.get("template") || q.get("template_id") || q.get("tid") ||
                 q.get("checklist") || q.get("checklist_id") || q.get("checklist_template_id") ||
                 from(location.state || {}, ["template", "template_id", "tid",
                                             "checklist", "checklist_id", "checklist_template_id", "templateId"]);
  const jobId = Number(jobRaw);
  const templateId = Number(tplRaw);
  return { jobId: Number.isFinite(jobId) ? jobId : null, templateId: Number.isFinite(templateId) ? templateId : null };
}

/** Build a nice per-equipment label e.g. "Giatsu 12000BTU Living room" */
const makeEquipLabel = (e) =>
  [e?.equipment, e?.location].map(v => (v || "").trim()).filter(Boolean).join(" ");

/** Compose a single-line site address from customer record fields */
function composeAddress(c) {
  if (!c) return "";
  const parts = [
    c.address, c.address2, c.address3, c.town, c.province, c.postcode
  ].map(x => (x || "").trim()).filter(Boolean);
  return parts.join(", ");
}

/** Compose a display name "First Surname" (fallbacks applied) */
function composeName(c) {
  const first = (c?.first_name || "").trim();
  const sur = (c?.surname || "").trim();
  const email = (c?.email || "").trim();
  return (first || sur) ? [first, sur].filter(Boolean).join(" ") : (email || `Customer #${c?.id ?? ""}`);
}

/** Lightweight client compression for photos */
async function compressImageBlobOrFile(src, { maxSide = 1600, quality = 0.7 } = {}) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = (e) => rej(e);
    if (src instanceof Blob) {
      const url = URL.createObjectURL(src);
      i.src = url;
      i._revoke = () => URL.revokeObjectURL(url);
    } else {
      i.src = src;
    }
  });

  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const cw = Math.round(w * scale);
  const ch = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, cw, ch);

  const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", quality));
  if (img._revoke) try { img._revoke(); } catch {}
  return blob;
}

export default function SR_EngineerChecklist() {
  const API_BASE = getApiBase();
  const location = useLocation();
  const { jobId: jobIdFromQuery } = extractIds(location);

  const [engineers, setEngineers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  // ---- Customer & job info ----
  const [customer, setCustomer] = useState("");
  const [address, setAddress] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [engineerId, setEngineerId] = useState("");
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");

  /**
   * items state:
   * {
   *   [taskId]: {
   *     completed: boolean,
   *     value_text?: string,                // single-equipment value
   *     valuesByEquip?: { [idx]: string }   // multi-equipment values
   *   }
   * }
   */
  const [items, setItems] = useState({});
  const [tplTaskMap, setTplTaskMap] = useState({});

  /* ------------------------------ Equipment ------------------------------ */
  const [equipment, setEquipment] = useState([
    { equipment: "", description: "", serial: "", location: "" },
  ]);

  const addEquipRow = () =>
    setEquipment(rows => [...rows, { equipment: "", description: "", serial: "", location: "" }]);

  const removeEquipRow = (idx) =>
    setEquipment(rows => rows.filter((_, i) => i !== idx));

  const changeEquip = (idx, field, value) =>
    setEquipment(rows => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  /* -------------------------------- Photos -------------------------------- */
  const [photos, setPhotos] = useState([]);

  async function onSelectPhotos(e) {
    const files = Array.from(e.target.files || []);
    const compressed = [];
    for (const f of files) {
      try {
        const blob = await compressImageBlobOrFile(f, { maxSide: 1600, quality: 0.7 });
        const cf = new File([blob], f.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
        compressed.push({
          file: cf,
          caption: "",
          previewUrl: URL.createObjectURL(blob),
        });
      } catch {
        compressed.push({
          file: f,
          caption: "",
          previewUrl: URL.createObjectURL(f),
        });
      }
    }
    setPhotos(prev => [...prev, ...compressed]);
    e.target.value = "";
  }

  function setPhotoCaption(idx, val) {
    setPhotos(prev => prev.map((p, i) => (i === idx ? { ...p, caption: val } : p)));
  }

  function removePhoto(idx) {
    setPhotos(prev => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      try { if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl); } catch {}
      return copy;
    });
  }

  /* --------------------------- Direct camera capture --------------------------- */
  const videoRef = useRef(null);
  const [camStream, setCamStream] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camBusy, setCamBusy] = useState(false);

  async function openCamera() {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false
      });
      setCamStream(s);
      setCamOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) { alert("Camera error: " + (e?.message || e)); }
  }

  function stopCamera() {
    try { camStream?.getTracks()?.forEach(t => t.stop()); } catch {}
    setCamStream(null); setCamOpen(false);
  }

  async function snapPhotoIntoList() {
    if (!videoRef.current) return;
    setCamBusy(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1600;
      canvas.height = video.videoHeight || 1200;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const raw = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.9));
      const blob = await compressImageBlobOrFile(raw, { maxSide: 1600, quality: 0.7 });
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
      const previewUrl = URL.createObjectURL(blob);

      setPhotos(prev => [...prev, { file, caption: "", previewUrl }]);
      stopCamera();
    } catch (e) {
      alert(String(e?.message || e));
    } finally {
      setCamBusy(false);
    }
  }

  useEffect(() => () => {
    photos.forEach(p => { try { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); } catch {} });
    try { camStream?.getTracks()?.forEach(t => t.stop()); } catch {}
  }, []); // eslint-disable-line

  /* ----------------------------- Load lists/data ----------------------------- */
  useEffect(() => {
    sr.engineers.list().then(rows => setEngineers(rows.filter(r => r.active)));
    sr.tasks.list().then(rows => setTasks(rows.filter(t => t.active)));
    sr.templates.list().then(rows => {
      const actives = rows.filter(t => t.active);
      setTemplates(actives);
      const { templateId: tFromParams } = extractIds(location);
      if (tFromParams && actives.some(t => t.id === tFromParams)) setTemplateId(String(tFromParams));
    });
  }, [location]);

  // customers list for non-calendar use
  const [customers, setCustomers] = useState([]);
  const [customerSelect, setCustomerSelect] = useState("manual"); // 'manual' | customerId
  const isFromCalendarJob = !!jobIdFromQuery;

  useEffect(() => {
    fetch("/api/customers")
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(arr => Array.isArray(arr) ? setCustomers(arr) : setCustomers([]))
      .catch(() => setCustomers([]));
  }, []);

  // Per-template ordering map
  useEffect(() => {
    if (!templateId) { setTplTaskMap({}); return; }
    (async () => {
      try {
        const t = await sr.templates.get(Number(templateId));
        const map = {};
        if (Array.isArray(t?.tasks) && t.tasks.length) {
          for (const x of t.tasks) map[x.task_id] = Number(x.sort_order) || 0;
        } else if (Array.isArray(t?.task_ids)) {
          for (const id of t.task_ids) map[id] = 0;
        }
        setTplTaskMap(map);
      } catch { setTplTaskMap({}); }
    })();
  }, [templateId]);

  // Prefill ONLY when we have a jobId (prevents 400 on template-only)
  useEffect(() => {
    const { jobId } = extractIds(location);
    if (!jobId) return;

    (async () => {
      try {
        const params = new URLSearchParams();
        params.set("job_id", jobId);
        if (templateId) params.set("template_id", Number(templateId));
        const url = `${API_BASE}/api/service-reports/prefill?${params.toString()}`;

        const res = await fetch(url);
        if (!res.ok) return; // quietly ignore 400/404
        const data = await res.json();

        if (data?.customer?.name) setCustomer(data.customer.name);
        if (data?.customer?.address) setAddress(data.customer.address);
        if (data?.job?.job_ref || data?.job?.quote_number) setJobRef(data.job.job_ref || data.job.quote_number);
        if (data?.job?.report_date) setDate(data.job.report_date);
        if (data?.template?.id) setTemplateId(String(data.template.id));

        const next = {};
        (data?.items || []).forEach(it => {
          next[Number(it.task_id)] = { completed: !!it.completed, value_text: it.value_text || "" };
        });
        if (Object.keys(next).length) setItems(next);

        if (Array.isArray(data?.equipment) && data.equipment.length) {
          const normed = data.equipment.map(r => ({
            equipment: (r.equipment ?? r.brand ?? "") || "",
            description: (r.description ?? r.model ?? "") || "",
            serial: r.serial || "",
            location: r.location || "",
          }));
          setEquipment(normed);
        }

        // lock dropdown into "manual" UX since this is a calendar prefill scenario
        setCustomerSelect("manual");
      } catch {}
    })();
  }, [API_BASE, location, templateId]);

  // Prefill from session handoff (kept as-is)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("SR_PREFILL");
      if (!raw) return;
      const data = JSON.parse(raw);

      if (data?.customer?.name) setCustomer(data.customer.name);
      if (data?.customer?.address) setAddress(data.customer.address);
      if (data?.job?.job_ref || data?.job?.quote_number) setJobRef(data.job.job_ref || data.job.quote_number);
      if (data?.job?.report_date) setDate(data.job.report_date);
      if (data?.template?.id) setTemplateId(String(data.template.id));

      const next = {};
      (data?.items || []).forEach(it => {
        next[Number(it.task_id)] = { completed: !!it.completed, value_text: it.value_text || "" };
      });
      if (Object.keys(next).length) setItems(next);

      if (Array.isArray(data?.equipment) && data.equipment.length) {
        const normed = data.equipment.map(r => ({
          equipment: (r.equipment ?? r.brand ?? "") || "",
          description: (r.description ?? r.model ?? "") || "",
          serial: r.serial || "",
          location: r.location || "",
        }));
        setEquipment(normed);
      }
      if (data?.job?.id) sessionStorage.setItem("sr_job", String(data.job.id));
      if (data?.template?.id) sessionStorage.setItem("sr_template", String(data.template.id));
      sessionStorage.removeItem("SR_PREFILL");
    } catch {}
  }, []);

  /* ---------------------------- Item state helpers ---------------------------- */
  function setCompleted(taskId, completed) {
    setItems(prev => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), completed } }));
  }
  function setSingleText(taskId, value) {
    setItems(prev => ({ ...prev, [taskId]: { ...(prev[taskId] || {}), value_text: value } }));
  }
  function setMultiText(taskId, equipIndex, value) {
    setItems(prev => {
      const cur = prev[taskId] || {};
      const nextMap = { ...(cur.valuesByEquip || {}) };
      nextMap[equipIndex] = value;
      return { ...prev, [taskId]: { ...cur, valuesByEquip: nextMap } };
    });
  }

  const visibleTasks = useMemo(() => {
    if (!templateId) return tasks.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id);
    const set = new Set(Object.keys(tplTaskMap).map(n => Number(n)));
    const filtered = tasks.filter(t => set.has(t.id));
    return filtered.sort((a, b) => {
      const sa = tplTaskMap[a.id] ?? a.sort_order ?? 0;
      const sb = tplTaskMap[b.id] ?? b.sort_order ?? 0;
      return sa - sb || a.id - b.id;
    });
  }, [templateId, tplTaskMap, tasks]);

  /* --------------------------------- Submit --------------------------------- */
  async function submit() {
    if (!customer.trim() || !address.trim() || !date || !engineerId) {
      return alert("Customer, address, date and engineer are required.");
    }
    const equipmentClean = equipment
      .map(r => ({
        equipment: (r.equipment || "").trim(),
        description: (r.description || "").trim(),
        serial: (r.serial || "").trim(),
        location: (r.location || "").trim(),
      }))
      .filter(r => r.equipment || r.description || r.serial || r.location);

    const multiEquip = equipmentClean.length > 1;

    const payloadItems = Object.entries(items).map(([task_id, v]) => {
      let value_text = null;

      if (multiEquip) {
        const arr = equipmentClean.map((eq, idx) => ({
          label: makeEquipLabel(eq) || `Item ${idx + 1}`,
          value: (v.valuesByEquip?.[idx] ?? "").toString().trim(),
        }));
        const anyVal = arr.some(x => x.value !== "");
        value_text = anyVal ? JSON.stringify({ per_equipment: arr }) : null;
      } else {
        const single = (v.value_text ?? "").toString().trim();
        value_text = single === "" ? null : single;
      }

      return { task_id: Number(task_id), completed: !!v.completed, value_text };
    });

    const payload = {
      customer_name: customer.trim(),
      site_address: address.trim(),
      job_ref: jobRef.trim() || null,
      report_date: date,
      engineer_id: Number(engineerId),
      signature_base64: signature || null,
      notes: notes.trim() || null,
      template_id: templateId ? Number(templateId) : null,
      items: payloadItems,
      equipment: equipmentClean,
    };

    try {
      const { id } = await sr.reports.create(payload);

      if (photos.length) {
        const fd = new FormData();
        photos.forEach(p => fd.append("photos", p.file));
        photos.forEach(p => fd.append("captions", p.caption || ""));
        const res = await fetch(`${API_BASE}/api/service-reports/reports/${id}/photos`, { method: "POST", body: fd });
        if (!res.ok) {
          console.warn("Photo upload failed", await res.text());
          alert("Report saved, but photo upload failed.");
        }
      }

      window.open(sr.reports.pdfUrl(id), "_blank");
    } catch (e) {
      console.error(e);
      alert("There was a problem creating the report.");
    }
  }

  /* ----------------------------------- UI ----------------------------------- */
  const engineerSelectStyle = { color: engineerId ? "#ffffff" : "#3c3c42" };
  const labelS = { fontSize: 12, fontWeight: 600, marginBottom: 4 };

  function TaskTextInputs({ task }) {
    const t = items[task.id] || {};
    const multi = (equipment.filter(r => r.equipment || r.description || r.serial || r.location).length || equipment.length) > 1;

    if (!multi) {
      const val = t.value_text ?? "";
      return (
        <input
          className="input"
          placeholder="Enter value…"
          value={val}
          onChange={e => setSingleText(task.id, e.target.value)}
        />
      );
    }

    return (
      <div>
        {equipment.map((eq, idx) => {
          const label = makeEquipLabel(eq) || (eq.equipment || `Item ${idx + 1}`).trim() || `Item ${idx + 1}`;
          const val = (items[task.id]?.valuesByEquip?.[idx] ?? "");
          return (
            <div key={idx}
                 style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#bbb" }}>{label}</div>
              <input
                className="input"
                placeholder="Enter value…"
                value={val}
                onChange={e => setMultiText(task.id, idx, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // handle picking a customer from dropdown (non-calendar use)
  function onPickCustomer(e) {
    const val = e.target.value;
    setCustomerSelect(val);
    if (val === "manual") return;
    const c = customers.find(x => String(x.id) === String(val));
    if (c) {
      setCustomer(composeName(c));
      setAddress(composeAddress(c));
    }
  }

  return (
    <div className="sr-fullbleed">
      <div className="card">
        <style>{`
          /* full-bleed wrapper to break out of any centered container */
          .sr-fullbleed {
            padding: 16px clamp(12px, 2vw, 24px);
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            width: 100vw;
            max-width: 100vw;
            background: var(--card, #0a2a57); /* match the dark form bg */
            min-height: 1px;
          }
          .muted { opacity: .8; }
          .sr-row { display:flex; gap:8px; flex-wrap: wrap; }
 
     @media (max-width: 719.98px) {
        .sr-fullbleed {
          position: static !important;
          left: auto !important;
          right: auto !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
      }

  @media (max-width: 719.98px) {
    /* Never allow sideways scroll */
    html, body { overflow-x: hidden; }
    .sr-fullbleed { overflow-x: hidden; }

    /* Ensure the main wrapper can't be wider than the viewport */
    .sr-wrap, .sr-card, .card {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
    }

    /* Grids collapse to single column */
    .sr-grid,
    .sr-grid.two,
    .sr-grid.three,
    .sr-grid.auto {
      display: grid;
      grid-template-columns: 1fr !important;
      gap: 12px;
    }

    /* Inputs/selects/textareas shrink correctly on small screens */
    .sr-field .input,
    .sr-field input,
    .sr-field select,
    .sr-field textarea,
    .sr-select,
    select,
    input,
    textarea {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;      /* cancel any desktop min-widths */
      box-sizing: border-box;
    }

    /* Action rows wrap neatly */
    .sr-actions,
    .btn-row,
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    /* Make long labels/values wrap instead of forcing width */
    .sr-label,
    .sr-value,
    .sr-row {
      white-space: normal !important;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    /* Tables: allow horizontal scroll if absolutely necessary */
    .sr-table, table.sr-table, .table-wrap {
      display: block;
      width: 100%;
      max-width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    /* Images/photos responsive */
    img, video, canvas {
      max-width: 100% !important;
      height: auto !important;
    }
    .sr-photos {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 8px;
    }
    .sr-photo { width: 100% !important; }

    /* Modals/dialogs */
    .sr-modal,
    .modal,
    .dialog {
      width: min(100vw, 560px) !important;
      max-width: 96vw !important;
      margin: 0 auto !important;
    }
  }

  @media (max-width: 719.98px) {
    /* Make all sizing include borders/padding */
    *, *::before, *::after { box-sizing: border-box; }

    /* Ensure page never exceeds the viewport width */
    html, body { margin: 0; padding: 0; max-width: 100vw; overflow-x: hidden; }

    /* Outer wrapper: no negative/full-bleed tricks; clamp side padding */
    .sr-fullbleed {
      position: static !important;
      left: auto !important;
      right: auto !important;
      margin: 0 !important;
      width: 100% !important;
      max-width: 100vw !important;
      padding-left: clamp(10px, 4vw, 16px) !important;
      padding-right: clamp(10px, 4vw, 16px) !important;
      overflow-x: hidden !important;
    }

    /* Card/container: don’t add extra width via borders + 100% */
    .sr-wrap, .sr-card, .card {
      width: 100% !important;
      max-width: calc(100vw - 2 * clamp(10px, 4vw, 16px)) !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    /* Kill any stray negative margins or fixed widths */
    [style*="margin-left: calc(50% - 50vw)"],
    [style*="width: 100vw"] {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 100% !important;
      max-width: 100vw !important;
    }
  }

        `}</style>

        <h2>Engineer Checklist</h2>

        <div className="sr-row">
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={labelS}>Template</div>
            <select className="input" value={templateId} onChange={e => setTemplateId(e.target.value)}>
              <option value="">— No template (show all tasks) —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id} style={{ color: "#3c3c42" }}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* -------- Customer area: calendar-prefill vs manual/select-from-list -------- */}
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Customer & Job</h3>

          {!isFromCalendarJob && (
            <div className="sr-row" style={{ marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={labelS}>Customer</div>
                <select className="input" value={customerSelect} onChange={onPickCustomer}>
                  <option value="manual">— Enter manually —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id} style={{ color: "#3c3c42" }}>
                      {(c.surname || "").trim()}, {(c.first_name || "").trim()}
                      {c.town ? ` — ${c.town}` : ""}
                    </option>
                  ))}
                </select>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Pick a customer to prefill name & address, or choose “Enter manually”.
                </div>
              </div>
            </div>
          )}

          {isFromCalendarJob && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Prefilled from calendar job.
            </div>
          )}

          <div className="sr-row" style={{ marginTop: 4 }}>
            <div style={{ flex: 1 }}>
              <div style={labelS}>Customer name</div>
              <input
                className="input"
                placeholder="Customer name"
                value={customer}
                onChange={e => setCustomer(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelS}>Site address</div>
              <input
                className="input"
                placeholder="Site address"
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </div>
          </div>

          <div className="sr-row" style={{ marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={labelS}>Job ref (optional)</div>
              <input className="input" placeholder="Job ref" value={jobRef} onChange={e => setJobRef(e.target.value)} />
            </div>
            <div>
              <div style={labelS}>Date</div>
              <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={labelS}>Engineer</div>
              <select className="input" value={engineerId} onChange={e => setEngineerId(e.target.value)} style={engineerSelectStyle}>
                <option value="" style={{ color: "#3c3c42" }}>Select engineer…</option>
                {engineers.map(e => (<option key={e.id} value={e.id} style={{ color: "#3c3c42" }}>{e.name}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* ------------------------------- Equipment ------------------------------- */}
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Equipment</h3>
          {equipment.map((row, idx) => {
            const gapY = 12;
            const fieldBox = (ph, field, isLast) => (
              <div style={{ marginBottom: isLast ? 0 : gapY }}>
                <input
                  className="input"
                  placeholder={ph}
                  value={row[field]}
                  onChange={e => changeEquip(idx, field, e.target.value)}
                />
              </div>
            );
            return (
              <div key={idx}
                   style={{
                     display: "grid",
                     gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                     gap: 8,
                     alignItems: "center",
                     marginBottom: 10
                   }}>
                {fieldBox("Equipment / Brand", "equipment", false)}
                {fieldBox("Description / Model", "description", false)}
                {fieldBox("Serial #", "serial", false)}
                <div style={{ display: "flex", gap: 8, marginBottom: 0 }}>
                  <input
                    className="input w-full"
                    placeholder="Location"
                    value={row.location}
                    onChange={e => changeEquip(idx, "location", e.target.value)}
                  />
                  {equipment.length > 1 && (
                    <button type="button" className="btn btn-danger px-2" onClick={() => removeEquipRow(idx)}>–</button>
                  )}
                </div>
              </div>
            );
          })}
          <button type="button" className="btn btn-secondary mt-2" onClick={addEquipRow}>Add item</button>
        </div>

        {/* --------------------------------- Tasks -------------------------------- */}
        <div className="card" style={{ marginTop: 16 }}>
          <h3>
            Tasks {templateId ? <span style={{ color: "#666" }}>({visibleTasks.length} from template)</span> : null}
          </h3>
          {visibleTasks.map(t => {
            const hasText = !!t.has_text;
            return (
              <div key={t.id}
                   style={{
                     display: "grid",
                     gridTemplateColumns: hasText ? "32px 1fr minmax(220px, 1fr)" : "32px 1fr",
                     gap: 12,
                     alignItems: "start",
                     marginBottom: 12
                   }}>
                <input
                  type="checkbox"
                  checked={!!items[t.id]?.completed}
                  onChange={e => setCompleted(t.id, e.target.checked)}
                />
                <div style={{ paddingTop: 8 }}>{t.title}</div>
                {hasText ? <TaskTextInputs task={t} /> : null}
              </div>
            );
          })}
        </div>

        {/* ------------------------------ Photos area ------------------------------ */}
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Photos</h3>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <label className="btn">
              Choose files
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={onSelectPhotos}
              />
            </label>

            {!camOpen && (
              <button type="button" className="btn" onClick={openCamera}>Use Camera</button>
            )}
            {camOpen && (
              <>
                <button type="button" className="btn btn-success" disabled={camBusy} onClick={snapPhotoIntoList}>
                  {camBusy ? "Capturing…" : "Take Photo"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={stopCamera}>Close Camera</button>
              </>
            )}
          </div>

          {camOpen && (
            <div style={{ marginBottom: 12 }}>
              <video ref={videoRef} playsInline autoPlay muted style={{ width: "100%", maxWidth: 480, borderRadius: 8 }} />
            </div>
          )}

          {photos.length > 0 && (
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
              {photos.map((p, idx) => (
                <div key={idx} className="card" style={{ padding: 8 }}>
                  <img src={p.previewUrl} alt="" style={{ width: "100%", borderRadius: 6 }} />
                  <input
                    className="input"
                    placeholder="Caption (optional)"
                    value={p.caption}
                    onChange={e => setPhotoCaption(idx, e.target.value)}
                    style={{ marginTop: 6 }}
                  />
                  <button className="btn btn-danger" style={{ marginTop: 6 }} onClick={() => removePhoto(idx)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ------------------------------ Signature ------------------------------ */}
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Signature</h3>
          <SRSignaturePad value={signature} onChange={setSignature} />
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3>Notes</h3>
          <textarea className="input" rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes…" />
        </div>

        <div className="sr-row" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={submit}>Save report & preview PDF</button>
        </div>
      </div>
    </div>
  );
}
